import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Heart, Compass, Sparkles, Shield, 
  ArrowRight, CheckCircle2, Users, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const coreValues = [
  {
    icon: Shield,
    title: 'Fearless Functionality',
    description: 'We build solutions that work under any circumstance. No shortcuts, no compromises on quality.',
    gradient: 'from-blue-500 to-indigo-500'
  },
  {
    icon: Target,
    title: 'Focused Execution',
    description: 'Clear objectives, measurable results. We stay locked in on what matters most to your success.',
    gradient: 'from-indigo-500 to-violet-500'
  },
  {
    icon: Sparkles,
    title: 'Fresh Design',
    description: 'Modern, intuitive, and purposeful. Technology should enhance experiences, not complicate them.',
    gradient: 'from-violet-500 to-purple-500'
  },
  {
    icon: Heart,
    title: 'Genuine Partnership',
    description: 'Your goals are our goals. We succeed when you succeed, and we stand by that commitment.',
    gradient: 'from-purple-500 to-pink-500'
  }
];

const philosophyPillars = [
  {
    title: 'Empowerment Over Dependency',
    description: 'We build systems that make you more capable, not more dependent on us. Our goal is to give you tools you can grow with.'
  },
  {
    title: 'Clarity Over Complexity',
    description: 'Technology should be understandable. We explain things plainly and build solutions that make sense.'
  },
  {
    title: 'Results Over Promises',
    description: 'We measure success by outcomes, not intentions. Every project is designed to deliver tangible value.'
  },
  {
    title: 'Community Over Competition',
    description: 'We believe in lifting others up. Through philanthropy and mentorship, we invest in the next generation.'
  }
];

const missionSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Mission & Philosophy - Fearless IT",
  "description": "Discover the mission, values, and philosophy that drive Fearless IT. We believe technology should empower businesses to grow, rank, and run flawlessly.",
  "author": {
    "@type": "Organization",
    "name": "Fearless IT"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const MissionPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="mission-page">
      <SEO 
        title="Mission & Philosophy - What Drives Fearless IT"
        description="Discover the mission, values, and philosophy behind Fearless IT. We believe technology should empower businesses to grow, rank, and run flawlessly. Based in Las Vegas, serving businesses everywhere."
        keywords="Fearless IT mission, company values, technology philosophy, Las Vegas IT company values, business technology partner, fearless functionality, focused execution, fresh design"
        url="/company/mission"
        structuredData={missionSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <Compass className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Our Purpose</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="mission-title">
            Mission & Philosophy
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #059669, #10B981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Technology That Serves People, Not the Other Way Around
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            We exist to help businesses harness technology in ways that are powerful, practical, and human-centered.
          </p>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 sm:p-12 border border-emerald-100">
            <div className="flex items-center gap-3 mb-6">
              <Target className="h-8 w-8 text-emerald-600" />
              <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">Our Mission</h2>
            </div>
            <p className="text-xl sm:text-2xl text-carbon-black leading-relaxed font-medium">
              To help businesses grow, rank, and run flawlessly through technology solutions that are powerful yet approachable, innovative yet reliable, and always focused on delivering real results.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Core Values
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              The principles that guide every project, every decision, and every interaction.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {coreValues.map((value, idx) => {
              const IconComponent = value.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${value.gradient} flex items-center justify-center mb-5`}>
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-carbon-black mb-3">{value.title}</h3>
                  <p className="text-slate-gray leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full mb-4">
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Our Philosophy</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                How We Think About Technology
              </h2>
              <div className="space-y-4 text-slate-gray leading-relaxed">
                <p>
                  We believe technology should be a force multiplier for human potential, not a barrier to it. Too many businesses are held back by systems that are confusing, unreliable, or simply don't fit their needs.
                </p>
                <p>
                  At Fearless IT, we approach every challenge with curiosity and pragmatism. We ask the right questions, listen carefully, and build solutions that address real problems, not just technical specifications.
                </p>
                <p>
                  Our philosophy is simple: if it doesn't help you succeed, we don't build it. Every feature, every line of code, every design decision is measured against one question: does this make our client's life better?
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              {philosophyPillars.map((pillar, idx) => (
                <div key={idx} className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-carbon-black mb-2">{pillar.title}</h3>
                      <p className="text-sm text-slate-gray leading-relaxed">{pillar.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Beyond Business
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Our mission extends beyond client work. We're committed to making a positive impact in our community.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Community Giving</h3>
              <p className="text-slate-gray text-sm leading-relaxed mb-4">
                We partner with the GodWork Foundation to support youth in Las Vegas through mentorship, technology training, and entrepreneurial education.
              </p>
              <Link to="/philanthropy" className="text-emerald-600 font-medium hover:text-emerald-700 transition-colors inline-flex items-center gap-1">
                Learn More <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Knowledge Sharing</h3>
              <p className="text-slate-gray text-sm leading-relaxed mb-4">
                We believe in sharing what we know. Through workshops, content, and mentorship, we help others grow their technology skills.
              </p>
              <Link to="/resources/guides" className="text-emerald-600 font-medium hover:text-emerald-700 transition-colors inline-flex items-center gap-1">
                View Resources <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Zap className="h-12 w-12 text-emerald-500 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Ready to Experience the Difference?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Partner with a team that's genuinely invested in your success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Start a Conversation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/growth-audit">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-emerald-400 text-emerald-600 hover:bg-emerald-50 transition-all w-full sm:w-auto">
                Free Growth Audit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default MissionPage;
